import React, { useEffect, useState } from 'react'

import { Portal } from 'govwell-ui'

import { KeyboardKey } from 'src/types'

const CONTENT_AREA_OVERLAY_TARGET_SELECTOR = '[data-staff-ai-overlay-target]'

type Props = {
  children: React.ReactNode
  isOpen: boolean
  onClose: () => void
}

/**
 * A reusable overlay component that renders content over the main content area,
 * leaving the sidebar visible. Uses a portal to render into the content container.
 *
 * The overlay has rounded corners matching the main content area design.
 */
const ContentAreaOverlay = ({ children, isOpen, onClose }: Props) => {
  const [container, setContainer] = useState<Element | null>(null)

  // Handle escape key to close
  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === KeyboardKey.Escape) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Handle clicks outside the overlay (e.g., on the sidebar) to close
  useEffect(() => {
    if (!isOpen || !container) {
      return
    }

    const handleClickOutside = (e: MouseEvent) => {
      // If the click target is not inside the overlay container, close the modal
      if (container && !container.contains(e.target as Node)) {
        onClose()
      }
    }

    // Use capture phase to catch clicks before they bubble
    document.addEventListener('click', handleClickOutside, true)
    return () => document.removeEventListener('click', handleClickOutside, true)
  }, [isOpen, container, onClose])

  // Find and cache the content container
  useEffect(() => {
    if (isOpen) {
      const contentContainer = document.querySelector(CONTENT_AREA_OVERLAY_TARGET_SELECTOR)
      setContainer(contentContainer)
    } else {
      setContainer(null)
    }
  }, [isOpen])

  if (!isOpen || !container) {
    return null
  }

  return (
    <Portal container={container}>
      <div className="animate-in absolute inset-0 z-50 flex flex-col overflow-hidden rounded-tl-lg rounded-tr-lg bg-white">
        {children}
      </div>
    </Portal>
  )
}

export default React.memo(ContentAreaOverlay)
